import { useMutation } from '@tanstack/react-query';
import type { PaymentIntent, Stripe, StripeError } from '@stripe/stripe-js';

import BILLING_STRIPE_PAYMENT_MUTATION_KEY from './mutationKey';

export interface StripeConfirmPaymentParams {
	/** Client secret from `beginPayment`. */
	clientSecret: string;
	/** The card the Payment Element collected, from `stripe.createConfirmationToken`. */
	confirmationToken: string;
}

/**
 * Confirms a Stripe PaymentIntent with a ConfirmationToken via `stripe.confirmPayment`.
 * Call from a component rendered inside `StripeElementsProvider`.
 *
 * Note - this is wraps a method from Stripe's SDK. This is not a method from the LW core api
 */
const useStripeConfirmPayment = (stripe: Stripe | null) => {
	return useMutation<
		PaymentIntent,
		StripeError | Error,
		StripeConfirmPaymentParams
	>({
		mutationKey: [BILLING_STRIPE_PAYMENT_MUTATION_KEY, 'confirmPayment'],
		mutationFn: async (params) => {
			if (!stripe) {
				throw new Error('stripe is not defined');
			}

			const result = await stripe.confirmPayment({
				clientSecret: params.clientSecret,
				confirmParams: {
					confirmation_token: params.confirmationToken,
					return_url: window.location.href,
				},
				redirect: 'if_required',
			});

			if (result.error) {
				throw result.error;
			}

			return result.paymentIntent;
		},
	});
};

export default useStripeConfirmPayment;
